import React, { useEffect } from "react";
import * as THREE from "three";
import { useGlobalStore } from "../../store";
import {
  attachmentMaterial,
  disposeObject,
  heatMapManager,
  isAdvancedHeatmaps,
  reluAlignmentVector,
  tpLookup,
} from "./Mesh";
import { globalBiteJump, myOTP } from "./Scene";
import { Jaw } from "./interfaces.ts";
import { TreatmentSetup } from "./TreatmentSetup.ts";
import { globalData } from "../../helpers/globData.ts";
import { addToIPRList } from "../../helpers/ipr.js";
import { addToothToPonticsArray } from "./controls/Pontics.js";
import { SingletonManager } from "../../helpers/singletonInitializer.ts";
let meshGroup2;
let meshGroup3;
export let rootGroup;
let isDone = false;

let perCam, ortCam;

export function changeCameraLayers(layersToRender) {
  // Disable all layers first (optional, but good for clear changes)

  perCam.current.layers.mask = 0; // Resets all layers to disabled
  ortCam.current.layers.mask = 0; // Resets all layers to disabled

  layersToRender.forEach(layer => {
    perCam.current.layers.enable(layer);
    ortCam.current.layers.enable(layer);
  });
}

export let splitTreatmentGeneration;
export function setMeshGroup3(group) {
  meshGroup3 = group;
  addPonticsToArray(group);
  cloneTeethGeometry();
  addIPRsToList(group);
}
export function setRootGroup(group) {
  rootGroup = group;

  rootGroup.children[0].material =
    SingletonManager.materialManager.getMaterial("boneMaterial");
  rootGroup.children[1].material =
    SingletonManager.materialManager.getMaterial("boneMaterial");
}
function addPonticsToArray(group) {
  for (let stepNum = 0; stepNum < group.children.length; stepNum++) {
    const step = group.children[stepNum];
    for (let isLower = 0; isLower < 2; isLower++) {
      const arch = step.children[isLower];

      for (
        let toothIndex = 0;
        toothIndex < arch.children.length;
        toothIndex++
      ) {
        const tooth = arch.children[toothIndex];

        if (tooth.userData["pontic"]) {
          addToothToPonticsArray(tooth);
        }
      }
    }
  }
}

function addIPRsToList(group) {
  for (let stepIndex = 0; stepIndex < group.children.length; stepIndex++) {
    for (let archIndex = 0; archIndex < 2; archIndex++) {
      const arch = group.children[stepIndex].children[archIndex];
      for (
        let toothIndex = 0;
        toothIndex < arch.children.length;
        toothIndex++
      ) {
        const iprGroup = arch.children[toothIndex].children[2];
        for (
          let iprIndex = 0;
          iprIndex < iprGroup.children.length;
          iprIndex++
        ) {
          addToIPRList(iprGroup.children[iprIndex]);
        }
      }
    }
  }
}

let teethMaterial;
export const occlusalSplitGroup = new THREE.Group();
occlusalSplitGroup.name = "occ_group";
function cloneTeethGeometry() {
  meshGroup3.children[0].children[0].children.forEach(tooth => {
    tooth.geometry = tooth.geometry.clone();
  });
  meshGroup3.children[0].children[1].children.forEach(tooth => {
    tooth.geometry = tooth.geometry.clone();
  });
}

function Split({
  maxStepNumber,
  toggle,
  GLBData,
  biteJumpsSteps,
  perspectiveCameraRef,
  orthographicCameraRef,
}) {
  const setRefs = useGlobalStore(state => state.setRefs);

  const groupRef2 = React.useRef(null);
  React.useEffect(() => {
    setRefs({
      groupRef2,
    });
  }, []);
  perCam = perspectiveCameraRef;
  ortCam = orthographicCameraRef;
  if (!teethMaterial) {
    teethMaterial = new THREE.MeshPhysicalMaterial({
      roughness: 0.4,
      reflectivity: 8,
      specularIntensity: 3,
      specularColor: "#2e2e2e",
      emissiveIntensity: 1,
      emissive: "#303030",
      color: "#fff9f9",
      side: THREE.DoubleSide,
      flatShading: false,
    });
  }
  if (toggle.occ) {
    teethMaterial.transparent = true;
    teethMaterial.opacity = 0.7;
    teethMaterial.side = THREE.FrontSide;
    teethMaterial.needsUpdate = true;
  } else {
    teethMaterial.transparent = false;
    teethMaterial.side = THREE.DoubleSide;
    teethMaterial.opacity = 1;
    teethMaterial.needsUpdate = true;
  }
  const gumMaterial = new THREE.MeshPhysicalMaterial({
    roughness: 0.35,
    reflectivity: 0.65,
    specularIntensity: 0.35,
    specularColor: "#ffe2e6",
    color: "#ff9090",
    emissive: "#ff93ab",

    emissiveIntensity: 0.15,
    side: THREE.DoubleSide,
  });
  function PrepareSplit(GLBData) {
    isDone = true;
    meshGroup2 = [];
    return;
    if (
      (GLBData[0].upper.teeth.length !== 0 &&
        GLBData[0].upper.teeth[0].children.length === 0) ||
      (GLBData[0].lower.teeth.length !== 0 &&
        GLBData[0].lower.teeth[0].children.length === 0)
    ) {
      console.log("GLB not ready");
      return;
    }
    let toothCopyCacher;

    let maxStepNumber = GLBData.length - 1 - (globalBiteJump ? 1 : 0);
    let modelGroup = new THREE.Group();
    let stepHolder, gumHolder, toothHolder;
    GLBData.forEach((gingiva, stepNumber) => {
      const isMaxStep = stepNumber === maxStepNumber;
      stepHolder = new THREE.Group();
      stepHolder.name = "step" + stepNumber;
      stepHolder.userData["step"] = stepNumber;
      tpLookup.splitStepsById[stepHolder.id] = stepHolder;
      modelGroup.add(stepHolder);

      gumHolder = new THREE.Mesh();

      if (gingiva.upper.gum?.name) {
        if (isMaxStep) {
          gumHolder = gingiva.upper.gum.clone();
        }
        gumHolder.userData["jaw"] = Jaw.Upper;
        gumHolder.userData["step"] = stepNumber;
        disposeObject(gingiva.upper.gum);
        gumHolder.castShadow = true;
        gumHolder.receiveShadow = true;
        gumHolder.material = gumMaterial;
        gumHolder.name = gingiva.upper.gum.name;
        gumHolder.visible = isMaxStep;
        tpLookup.splitGumsById[gumHolder.id] = gumHolder;
        let toothNumber;
        gingiva.upper.teeth.forEach(tooth => {
          toothHolder = isMaxStep ? tooth : new THREE.Mesh();
          toothHolder.castShadow = true;
          toothHolder.receiveShadow = true;
          toothHolder.material = teethMaterial;
          toothHolder.name = tooth.name;

          toothNumber = parseInt(
            toothHolder.name.split("_")[2].substring(1, 4),
          );

          tooth.children[0].children.forEach(attachment => {
            attachment.castShadow = true;
            attachment.receiveShadow = true;
            attachment.name = attachment.name + "_att";
            attachment.visible = true;
            attachment.material = attachmentMaterial;
          });
          toothCopyCacher = toothHolder.clone();
          toothCopyCacher.userData["number"] = toothNumber;
          toothCopyCacher.userData["jaw"] = Jaw.Upper;
          toothCopyCacher.userData["step"] = stepNumber;
          toothCopyCacher.userData["isTooth"] = true;

          disposeObject(toothHolder);
          tpLookup.splitTeethById[toothCopyCacher.id] = toothCopyCacher;
          gumHolder.add(toothCopyCacher);
        });
      }

      stepHolder.add(gumHolder);

      gumHolder = new THREE.Mesh();

      if (gingiva.lower.gum?.name) {
        if (isMaxStep) {
          gumHolder = gingiva.lower.gum.clone();
        }
        gumHolder.userData["jaw"] = Jaw.Lower;
        gumHolder.userData["step"] = stepNumber;
        disposeObject(gingiva.lower.gum);
        gumHolder.castShadow = true;
        gumHolder.receiveShadow = true;
        gumHolder.material = gumMaterial;
        gumHolder.name = gingiva.lower.gum.name;
        gumHolder.visible = isMaxStep;
        tpLookup.splitGumsById[gumHolder.id] = gumHolder;
        let toothNumber;
        gingiva.lower.teeth.forEach(tooth => {
          toothHolder = isMaxStep ? tooth : new THREE.Mesh();
          toothHolder.castShadow = true;
          toothHolder.receiveShadow = true;
          toothHolder.material = teethMaterial;

          toothHolder.name = tooth.name;
          toothNumber = parseInt(
            toothHolder.name.split("_")[2].substring(1, 4),
          );

          tooth.children[0].children.forEach(attachment => {
            attachment.castShadow = true;
            attachment.receiveShadow = true;
            attachment.name = attachment.name + "_att";
            attachment.visible = true;
            attachment.material = attachmentMaterial;
          });
          toothCopyCacher = toothHolder.clone();

          toothCopyCacher.userData["number"] = toothNumber;
          toothCopyCacher.userData["jaw"] = Jaw.Lower;
          toothCopyCacher.userData["step"] = stepNumber;
          toothCopyCacher.userData["isTooth"] = true;

          disposeObject(toothHolder);
          tpLookup.splitTeethById[toothCopyCacher.id] = toothCopyCacher;
          gumHolder.add(toothCopyCacher);
        });
      }
      stepHolder.add(gumHolder);
    });

    return modelGroup;
  }

  const centering_group = new THREE.Group();
  let center = { x: 0, y: 0, z: 0 };
  useEffect(() => {
    for (let archIndex = 0; archIndex < 2; archIndex++) {
      let child = groupRef2.current.children[maxStepNumber].children[archIndex];
      if (!child?.name) {
        break;
      }
      if (child?.name.includes("st")) {
        centering_group.add(child.clone());
      }
    }

    // centering mesh (offset)
    const aabb = new THREE.Box3();
    aabb.setFromObject(centering_group);
    center = new THREE.Vector3(0, 0, 0);
    aabb.getCenter(center);
    groupRef2.current.position.set(-center.x, -center.y, -center.z);
    if (rootGroup) {
      rootGroup.position.set(-center.x, -center.y, -center.z);
      rootGroup.position.add(reluAlignmentVector);
    }
    occlusalSplitGroup.position.copy(groupRef2.current.position);
    // }
  }, [groupRef2.current]);

  if (!meshGroup2 && !isDone) {
    meshGroup2 = PrepareSplit(GLBData);
    meshGroup3.children.forEach(step => {
      step.visible = false;
      step.children[0].visible = true;
      step.children[1].visible = true;
    });
    splitTreatmentGeneration = new TreatmentSetup(
      [
        ...meshGroup3.children[0].children[0].children,
        ...meshGroup3.children[0].children[1].children,
      ],
      myOTP,
      meshGroup3.children,
      "Split",
    );
    if (isAdvancedHeatmaps) {
      splitTreatmentGeneration.heatmaps = heatMapManager.getHeatmapsResult();
    }
    splitTreatmentGeneration.moveToStep(globalData.maxStepNumber);
  } else {
    if (meshGroup3) {
      meshGroup3.visible = toggle.split || toggle.compareModifications;
    }
  }

  return meshGroup3 ? (
    <group>
      <primitive ref={groupRef2} object={meshGroup3} />
      {rootGroup ? <primitive object={rootGroup} /> : <></>}
    </group>
  ) : (
    <></>
  );
}

export default Split;
